import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { withdrawDocument } from "services/upload-file.service";
import { Button as PolarisButton, InlineStack } from "@shopify/polaris";
import { Button } from "catalyst-ui-kit/button";
import { Textarea } from "catalyst-ui-kit/textarea";
import { toast } from "sonner";

// Kept in sync manually with the backend's withdraw reason limit (mirrors
// AnnulDocumentConfirm's REASON_MAX_LENGTH — no shared constants module).
const REASON_MAX_LENGTH = 2000;

interface WithdrawDocumentConfirmProps {
  fileId: string;
  /** Caller-specific refresh: which redux slice/thunk to refetch after a
   * successful withdraw differs between the document page and the profile
   * ("authorized file") page, so this modal does not hardcode either. */
  onDone?: () => void;
}

const WithdrawDocumentConfirm: React.FC<WithdrawDocumentConfirmProps> = ({
  fileId,
  onDone,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();

  const [reason, setReason] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleConfirm = async () => {
    if (
      !companyId ||
      !fileId ||
      reason.trim().length > REASON_MAX_LENGTH ||
      isLoading
    )
      return;
    try {
      setIsLoading(true);
      setErrMsg(null);
      await withdrawDocument(companyId, fileId, reason.trim() || undefined);
      toast.success(t("withdraw_document_success"));
      onDone?.();
      closeModal();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.message || "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {errMsg && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {t(errMsg)}
        </div>
      )}
      <h1 style={{ textAlign: "center", marginBottom: "8px" }}>
        {t("withdraw_document_confirm_title")}
      </h1>
      <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
        <li>{t("withdraw_document_effect_unsigned")}</li>
        <li>{t("withdraw_document_effect_notice")}</li>
        <li>{t("withdraw_document_effect_permanent")}</li>
      </ul>
      <div>
        <label
          htmlFor="withdraw-document-reason"
          className="mb-1 block text-sm font-medium text-zinc-950 dark:text-white"
        >
          {t("withdraw_document_reason_label")}
        </label>
        <Textarea
          id="withdraw-document-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("withdraw_document_reason_placeholder")}
          rows={3}
          maxLength={REASON_MAX_LENGTH}
        />
        <p className="mt-1 text-right text-xs text-zinc-500">
          {reason.length}/{REASON_MAX_LENGTH}
        </p>
      </div>
      <InlineStack align="space-between">
        <PolarisButton
          onClick={handleConfirm}
          loading={isLoading}
          disabled={reason.trim().length > REASON_MAX_LENGTH}
          tone="critical"
          variant="primary"
        >
          {t("Yes")}
        </PolarisButton>
        <Button plain onClick={closeModal} disabled={isLoading}>
          {t("No")}
        </Button>
      </InlineStack>
    </div>
  );
};

export default WithdrawDocumentConfirm;
